import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Box,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Vocabulary } from "../../Utils/Vocabulary";
import withRole from "../../Hooks/withRole";
import { SupplimentaryHoursProps } from "../../Utils/Types";
import { useEffect } from "react";
import { SupplimentaryHoursFormDefaultState } from "../../Utils/Models";

function SupplimentaryHoursForm(props: SupplimentaryHoursProps) {
  const {
    suplimentaryHour,
    setSupplimentaryHour,
    errors,
    supplementaryHourPrice,
  } = props;
  const paymentMethods = props.paymentMethods;

  const onChange = (e: any, name: string) => {
    setSupplimentaryHour({ ...suplimentaryHour, [name]: e.target.value });
  };

  const handleIncrement = () => {
    setSupplimentaryHour({
      ...suplimentaryHour,
      nrOfHours: (Number(suplimentaryHour.nrOfHours) || 0) + 1,
    });
  };

  const handleDecrement = () => {
    const newValue = (Number(suplimentaryHour.nrOfHours) || 0) - 1;
    if (newValue >= 0) {
      setSupplimentaryHour({
        ...suplimentaryHour,
        nrOfHours: newValue,
      });
    }
  };

  /**
   * set default state of modal when opened
   */
  useEffect(() => {
    setSupplimentaryHour(new SupplimentaryHoursFormDefaultState());
  }, []);

  const pricePerHour = Number(supplementaryHourPrice ?? 0);
  const nrOfHours = Number(suplimentaryHour.nrOfHours) || 0;
  const totalPrice = pricePerHour * nrOfHours;

  return (
    <div style={{ padding: "16px" }}>
      <Grid container spacing={3}>
        {/* Price per hour - Read only as text, full width */}
        <Grid item xs={12}>
          <Box
            sx={{
              p: 3,
              backgroundColor: "#f8f9fa",
              borderRadius: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border: "1px solid #e8e8e8",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                fontSize: "0.95rem",
              }}
            >
              {Vocabulary.pricePerHour}
            </Typography>
            <Typography
              sx={{ fontWeight: 700, fontSize: "1rem", color: "primary.main" }}
            >
              {pricePerHour} RON
            </Typography>
          </Box>
        </Grid>

        {/* Number of hours - Editable with +/- buttons, full width */}
        <Grid item xs={12}>
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 1.5,
                display: "block",
                fontWeight: 400,
              }}
            >
              {Vocabulary.nrOfSupplimentaryHours}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDecrement}
                sx={{
                  minWidth: "40px",
                  height: "40px",
                  p: 0,
                  borderRadius: 1.5,
                }}
              >
                <RemoveIcon />
              </Button>
              <TextField
                value={suplimentaryHour.nrOfHours || 0}
                onChange={(e) => onChange(e, "nrOfHours")}
                type="number"
                error={Boolean(errors.nrOfHours)}
                inputProps={{ min: 0, style: { textAlign: "center" } }}
                sx={{
                  flex: 1,
                  "& input": {
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    padding: "12px",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleIncrement}
                sx={{
                  minWidth: "40px",
                  height: "40px",
                  p: 0,
                  borderRadius: 1.5,
                }}
              >
                <AddIcon />
              </Button>
            </Box>
            {errors.nrOfHours && (
              <Typography
                variant="caption"
                sx={{ color: "error.main", display: "block", mt: 1 }}
              >
                {errors.nrOfHours}
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Payment method - Editable */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>{Vocabulary.paymentMethod}</InputLabel>
            <Select
              label={`${Vocabulary.paymentMethod}`}
              value={suplimentaryHour.paymentMethod}
              onChange={(e) => onChange(e, "paymentMethod")}
            >
              {paymentMethods?.map((option: any) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Total Price Summary */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Box
            sx={{
              p: 2,
              backgroundColor: "#f0f0f0",
              borderRadius: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border: "1px solid #e0e0e0",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "text.secondary" }}
            >
              {Vocabulary.total}
            </Typography>
            <Typography
              sx={{ fontWeight: 600, fontSize: "1.1rem", color: "text.primary" }}
            >
              {totalPrice.toFixed(2)} RON
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </div>
  );
}

export default withRole(SupplimentaryHoursForm);
